import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Order, OrderAction, OrderItem } from '../types/myaccount';
import api from '../services/api';
import { PageShellLoading, TableSkeleton, ToolbarSkeleton } from './MyAccountLoadingStates';
import DataTable, { type DataTableColumn } from './DataTable';
import {accountActionIconGroupStyle, accountActionIconStyle} from './accountActionIconStyles';
import MyAccountPageShell from './MyAccountPageShell';
import { formatMoneyWithCurrencyCode } from '../utils/formatMoneyWithCurrencyCode';
import { getStatusBadgeClassName } from '../utils/statusBadges';

interface MyAccountOrderHistoryProps {
    perPage?: number;
    userName?: string;
    wekaPagePermissions?: Record<string, boolean>;
}

const MyAccountOrderHistory: React.FC<MyAccountOrderHistoryProps> = ({
    perPage = 25,
    userName = 'User',
    wekaPagePermissions = {},
}) => {
    const [orders, setOrders] = useState<Order[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(perPage);
    const [searchTerm, setSearchTerm] = useState('');
    const canReorderOrder = wekaPagePermissions.reorder_order !== false;

    const fetchOrders = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await api.getOrders({ page: 1, per_page: 100 });
            setOrders(data);
        } catch (err) {
            console.error('Error fetching orders:', err);
            setError('Failed to load orders. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getOrderNumber = (order: Order): string => {
        return order.purchase_order_no || order.id.toString();
    };

    const getOrderLinkLabel = (order: Order): string => {
        const erpOrderId = order.erp_order_id;
        const hasValidErpOrderId = Boolean(
            erpOrderId && !['no', 'processing', '0', ''].includes(erpOrderId)
        );

        return hasValidErpOrderId && erpOrderId ? erpOrderId : 'Processing';
    };

    const getOrderActions = (order: Order): OrderAction[] => {
        if (Array.isArray(order.account_actions) && order.account_actions.length > 0) {
            return order.account_actions.filter((action) => (
                action
                && action.url
                && action.key !== 'view'
                && (canReorderOrder || action.key !== 'order_again')
            ));
        }

        if (canReorderOrder && order.reorder_url) {
            return [{
                key: 'order_again',
                label: 'Order again',
                url: order.reorder_url,
                icon: 'fas fa-redo',
            }];
        }

        return [];
    };

    const formatOrderTotal = (order: Order): string => formatMoneyWithCurrencyCode(
        order.total,
        order.currency,
        order.currency_display_mode ?? order.show_currency_code,
        order.currency_symbol,
    );

    if (loading && !orders) {
        return (
            <PageShellLoading
                showMeta={true}
                titleWidth="24%"
                subtitleWidth="54%"
                metaWidth="132px"
            >
                <ToolbarSkeleton />
                <TableSkeleton columns={5} rows={10} />
            </PageShellLoading>
        );
    }

    if (error) {
        return (
            <MyAccountPageShell
                title="Order History"
                subtitle="Review your latest orders and reorder past purchases."
                meta={`Account: ${userName}`}
            >
                <div className="woocommerce-error" role="alert">{error}</div>
                <button onClick={fetchOrders} className="button">Retry</button>
            </MyAccountPageShell>
        );
    }

    if (!orders || orders.length === 0) {
        return (
            <MyAccountPageShell
                title="Order History"
                subtitle="Review your latest orders and reorder past purchases."
                meta={`Account: ${userName}`}
            >
                    <div className="woocommerce-message woocommerce-message--info woocommerce-Message woocommerce-Message--info woocommerce-info">
                        No orders have been made yet.
                    </div>
                    <Link to="/shop" className="button">Browse products</Link>
            </MyAccountPageShell>
        );
    }

    const filteredOrders = orders.filter(order => {
        if (!searchTerm) return true;
        const orderNumber = getOrderNumber(order).toLowerCase();
        const orderItems = order.line_items || order.items || [];
        const description = orderItems.map((item: OrderItem) => item.name).join(' ').toLowerCase();
        const customerText = [
            order.erp_customer_id,
            order.customer_display_name,
            order.customer_id?.toString(),
        ].filter(Boolean).join(' ').toLowerCase();
        return orderNumber.includes(searchTerm.toLowerCase())
            || description.includes(searchTerm.toLowerCase())
            || customerText.includes(searchTerm.toLowerCase());
    });
    const showCustomerIdColumn = orders.some((order) => order.is_child_record === true);
    const columns: DataTableColumn<Order>[] = [
        {
            id: 'order_number',
            header: 'ORDER #',
            mobileLabel: 'Order #',
            mobileDisplay: 'title',
            headerStyle: { padding: '12px', textAlign: 'left', fontWeight: '600', color: 'var(--weka-muted-text-color)' },
            cellStyle: { padding: '12px' },
            render: (order) => (
                <Link
                    to={`/view-order/${order.id}`}
                    className="weka-account-table-link text-decoration-none"
                >
                    {getOrderLinkLabel(order)}
                </Link>
            ),
        },
        {
            id: 'order_description',
            header: 'ORDER DESCRIPTION',
            mobileLabel: 'Items',
            mobileDisplay: 'field',
            headerStyle: { padding: '12px', textAlign: 'left', fontWeight: '600', color: 'var(--weka-muted-text-color)' },
            cellStyle: { padding: '12px' },
            render: (order) => {
                const orderItems = order.line_items || order.items || [];
                return orderItems.length > 0
                    ? orderItems.map((item: OrderItem) => `${item.name} x${item.quantity}`).join(', ')
                    : 'N/A';
            },
        },
        ...(showCustomerIdColumn ? [{
            id: 'customer_id',
            header: 'CUSTOMER ID',
            mobileLabel: 'Customer ID',
            mobileDisplay: 'field' as const,
            headerStyle: { padding: '12px', textAlign: 'left' as const, fontWeight: '600', color: 'var(--weka-muted-text-color)' },
            cellStyle: { padding: '12px' },
            render: (order: Order) => order.erp_customer_id || order.customer_id?.toString() || '-',
        }] : []),
        {
            id: 'order_date',
            header: 'ORDER DATE',
            mobileLabel: 'Date',
            mobileDisplay: 'field',
            headerStyle: { padding: '12px', textAlign: 'left', fontWeight: '600', color: 'var(--weka-muted-text-color)' },
            cellStyle: { padding: '12px' },
            render: (order) => formatDate(order.date_created),
        },
        {
            id: 'status',
            header: 'STATUS',
            mobileLabel: 'Status',
            mobileDisplay: 'field',
            headerStyle: { padding: '12px', textAlign: 'left', fontWeight: '600', color: 'var(--weka-muted-text-color)' },
            cellStyle: { padding: '12px' },
            render: (order) => (
                <span className={getStatusBadgeClassName('orders', order.status)}>
                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                </span>
            ),
        },
        {
            id: 'total',
            header: 'TOTAL',
            mobileLabel: 'Total',
            mobileDisplay: 'field',
            headerStyle: { padding: '12px', textAlign: 'left', fontWeight: '600', color: 'var(--weka-muted-text-color)' },
            cellStyle: { padding: '12px' },
            render: (order) => formatOrderTotal(order),
        },
        {
            id: 'action',
            header: 'ACTION',
            mobileDisplay: 'actions',
            headerStyle: { padding: '12px', textAlign: 'left', fontWeight: '600', color: 'var(--weka-muted-text-color)' },
            cellStyle: { padding: '12px' },
            render: (order) => {
                const actions = getOrderActions(order);
                if (actions.length === 0) {
                    return null;
                }

                return (
                    <div style={accountActionIconGroupStyle}>
                        {actions.map((action) => (
                            <a
                                key={`${order.id}-${action.key}`}
                                href={action.url}
                                title={action.label}
                                aria-label={action.label}
                                style={accountActionIconStyle}
                            >
                                <i className={action.icon || 'fas fa-circle'} aria-hidden="true"></i>
                            </a>
                        ))}
                    </div>
                );
            },
        },
    ];

    return (
        <MyAccountPageShell
            title="Order History"
            subtitle="Review your latest orders and reorder past purchases."
            meta={`Account: ${userName}`}
        >
                {!canReorderOrder && (
                    <div className="alert alert-info" role="status">
                        Reordering orders is disabled for this page.
                    </div>
                )}
                <DataTable
                    rows={filteredOrders}
                    rowKey={(order) => order.id}
                    wrapperClassName=""
                    responsiveMode="stacked-cards"
                    tableStyle={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}
                    toolbar={{
                        searchValue: searchTerm,
                        onSearchChange: (value) => {
                            setSearchTerm(value);
                            setCurrentPage(1);
                        },
                        entriesPerPage,
                        onEntriesPerPageChange: (value) => {
                            setEntriesPerPage(value);
                            setCurrentPage(1);
                        },
                        searchPlaceholder: 'Search...',
                    }}
                    pagination={{
                        currentPage,
                        itemsPerPage: entriesPerPage,
                        totalItems: filteredOrders.length,
                        itemsLabel: 'entries',
                        onPageChange: setCurrentPage,
                        mode: 'simple',
                    }}
                    rowStyle={{
                        borderBottom: '1px solid var(--weka-subtle-border-color)',
                    }}
                    columns={columns}
                />
        </MyAccountPageShell>
    );
};

export default MyAccountOrderHistory;
